import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ArrowRight, Check, X, MessageSquareText, CalendarClock, Ban, ChevronDown } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { ShiftSwapRequestView } from '../../../../models/shiftSwap';
import StatusBadge from './StatusBadge';
import SwapAvatar from './SwapAvatar';
import SwapTimeline from './SwapTimeline';
import {
  formatShiftDate,
  formatShiftTimeRange,
  formatLocalShiftDate,
  formatLocalShiftTimeRange,
  formatRelativeTime,
} from './format';

export type SwapAction = 'accept' | 'decline' | 'cancel' | 'approve' | 'reject';

interface SwapCardProps {
  swap: ShiftSwapRequestView;
  currentUserId: string;
  // 'readonly' renders the card with no action buttons (used by the manager/HR team-history view).
  mode: 'mine' | 'approval' | 'readonly';
  busyAction?: SwapAction | null;
  locale?: string;
  onAccept?: (swap: ShiftSwapRequestView) => void;
  onDecline?: (swap: ShiftSwapRequestView) => void;
  onCancel?: (swap: ShiftSwapRequestView) => void;
  onApprove?: (swap: ShiftSwapRequestView) => void;
  onReject?: (swap: ShiftSwapRequestView) => void;
}

interface PartyShiftProps {
  name: string;
  imageUrl?: string | null;
  caption: string;
  startTime?: string | null;
  endTime?: string | null;
  // Company-local wall-clock strings; preferred over UTC when present.
  localStart?: string | null;
  localEnd?: string | null;
  emptyLabel?: string;
  locale?: string;
}

const PartyShift: React.FC<PartyShiftProps> = ({
  name,
  imageUrl,
  caption,
  startTime,
  endTime,
  localStart,
  localEnd,
  emptyLabel,
  locale,
}) => {
  const hasShift = !!(startTime && endTime) || !!(localStart && localEnd);
  // Prefer the timezone-safe local wall-clock; fall back to the UTC field only when
  // the server did not supply a local one (e.g. a one-way cover leg).
  const dateLabel = localStart ? formatLocalShiftDate(localStart, locale) : startTime ? formatShiftDate(startTime, locale) : '';
  const rangeLabel =
    localStart && localEnd
      ? formatLocalShiftTimeRange(localStart, localEnd, locale)
      : startTime && endTime
        ? formatShiftTimeRange(startTime, endTime, locale)
        : '';

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2.5">
        <SwapAvatar name={name} imageUrl={imageUrl} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{caption}</p>
        </div>
      </div>

      {hasShift ? (
        <div className="mt-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <CalendarClock size={14} className="shrink-0 text-muted-foreground" />
            {dateLabel}
          </div>
          <p className="mt-0.5 ps-[22px] text-xs text-muted-foreground tabular-nums">
            {rangeLabel}
          </p>
        </div>
      ) : (
        <div className="mt-2.5 flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          <Ban size={14} className="shrink-0" />
          {emptyLabel}
        </div>
      )}
    </div>
  );
};

const SwapCard: React.FC<SwapCardProps> = ({
  swap,
  currentUserId,
  mode,
  busyAction,
  locale,
  onAccept,
  onDecline,
  onCancel,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [showTimeline, setShowTimeline] = useState(false);

  const isRequester = swap.requesterUserId === currentUserId;
  const isCounterparty = swap.counterpartyUserId === currentUserId;
  const isParty = isRequester || isCounterparty;
  const isOneWay = !swap.counterpartyShiftStartTime || !swap.counterpartyShiftEndTime;
  const canCancel = isRequester && (swap.status === 'Pending' || swap.status === 'PeerAccepted');
  // Approved with no human approver = the swap auto-applied when the peer accepted. Prefer the explicit
  // server flag; fall back to the (older) "no manager name" inference for rows created before it existed.
  const autoApplied = swap.autoApplied || (swap.status === 'Approved' && !swap.managerName);
  const busy = !!busyAction;
  const relTime = formatRelativeTime(swap.createdOn, locale, undefined, t('Just now'));

  const label = (name: string, userId: string) => (userId === currentUserId ? t('You') : name);

  // Resolve each party's leg once (name already "You"-aware), then arrange them from the viewer's
  // point of view: leg1 = what the viewer gives, leg2 = what the viewer gets. A one-way cover has
  // one empty leg (the side that contributes no shift).
  const requesterLeg = {
    name: label(swap.requesterName, swap.requesterUserId),
    imageUrl: swap.requesterImageUrl,
    startTime: swap.requesterShiftStartTime,
    endTime: swap.requesterShiftEndTime,
    localStart: swap.requesterShiftLocalStart,
    localEnd: swap.requesterShiftLocalEnd,
  };
  const counterpartyLeg = {
    name: label(swap.counterpartyName, swap.counterpartyUserId),
    imageUrl: swap.counterpartyImageUrl,
    startTime: swap.counterpartyShiftStartTime,
    endTime: swap.counterpartyShiftEndTime,
    localStart: swap.counterpartyShiftLocalStart,
    localEnd: swap.counterpartyShiftLocalEnd,
  };
  const counterpartyEmpty = { name: counterpartyLeg.name, imageUrl: counterpartyLeg.imageUrl };

  let leg1: PartyShiftProps;
  let leg2: PartyShiftProps;
  if (isParty) {
    if (isRequester) {
      leg1 = { ...requesterLeg, caption: t('You give') };
      leg2 = isOneWay
        ? { ...counterpartyEmpty, caption: t('You get'), emptyLabel: t('Nothing in return (one-way cover)') }
        : { ...counterpartyLeg, caption: t('You get') };
    } else {
      // Viewer is the counterparty: for a one-way cover they give nothing and take the requester's shift.
      leg1 = isOneWay
        ? { ...counterpartyEmpty, caption: t('You give'), emptyLabel: t('Nothing (one-way cover)') }
        : { ...counterpartyLeg, caption: t('You give') };
      leg2 = { ...requesterLeg, caption: t('You get') };
    }
  } else {
    // Neutral approver / history framing: requester gives, counterparty gives or covers.
    leg1 = { ...requesterLeg, caption: t('{{name}} gives', { name: requesterLeg.name }) };
    leg2 = isOneWay
      ? { ...counterpartyEmpty, caption: t('Covers the shift'), emptyLabel: t('One-way cover, no return shift') }
      : { ...counterpartyLeg, caption: t('Gives this shift') };
  }

  return (
    <article
      className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm transition-colors hover:border-foreground/15"
      data-testid={`swap-card-${swap.id}`}
    >
      {/* Header: context + status */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-foreground">
              {isRequester
                ? t('Your swap request')
                : t('{{name}} requested a swap', { name: swap.requesterName })}
            </p>
            {isOneWay && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                data-testid={`swap-oneway-${swap.id}`}
              >
                <Ban size={12} className="shrink-0" />
                {t('One-way cover')}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('Requested on')} {formatShiftDate(swap.createdOn, locale)}
            {relTime ? ` · ${relTime}` : ''}
          </p>
        </div>
        <StatusBadge status={swap.status} />
      </div>

      {/* Exchange, framed from the viewer's point of view (You give -> You get). Stacks vertically on
          mobile so each leg stays legible; the connector rotates to point top->bottom there. */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:gap-4">
        <PartyShift {...leg1} locale={locale} />

        <div className="flex items-center justify-center sm:flex-col sm:pt-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground rotate-90 sm:rotate-0">
            {isOneWay ? <ArrowRight size={16} className="rtl:-scale-x-100" /> : <ArrowLeftRight size={16} />}
          </div>
        </div>

        <PartyShift {...leg2} locale={locale} />
      </div>

      {/* Reason */}
      {swap.reason && (
        <div className="mt-4 flex gap-2 rounded-lg bg-muted/40 px-3 py-2">
          <MessageSquareText size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-sm text-foreground/90">{swap.reason}</p>
        </div>
      )}

      {/* Manager decision note. An auto-applied swap (Approved with no human
          approver) shows an explicit note instead of a misleading "Manager" label. */}
      {(swap.status === 'Approved' || swap.status === 'Rejected') && (swap.managerComments || autoApplied) && (
        <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2">
          <p className="text-xs font-medium text-foreground">
            {autoApplied ? t('Applied automatically on acceptance') : swap.managerName || t('Manager')}
          </p>
          {swap.managerComments && (
            <p className="mt-0.5 text-sm text-foreground/90">{swap.managerComments}</p>
          )}
        </div>
      )}

      {/* Lifecycle details, disclosed on demand to keep the card compact. */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowTimeline((v) => !v)}
          aria-expanded={showTimeline}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid={`swap-details-toggle-${swap.id}`}
        >
          <ChevronDown size={14} className={`transition-transform ${showTimeline ? 'rotate-180' : ''}`} />
          {t('Details')}
        </button>
        {showTimeline && (
          <div className="mt-3 rounded-lg border border-border bg-muted/20 px-3 py-3">
            <SwapTimeline swap={swap} locale={locale} autoApplied={autoApplied} />
          </div>
        )}
      </div>

      {/* Actions */}
      {mode === 'mine' && (swap.awaitingMyAcceptance || canCancel) && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          {swap.awaitingMyAcceptance && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                loading={busyAction === 'decline'}
                onClick={() => onDecline?.(swap)}
                data-testid={`swap-decline-${swap.id}`}
              >
                <X size={15} className="me-1.5" />
                {t('Decline')}
              </Button>
              <Button
                variant="success"
                size="sm"
                disabled={busy}
                loading={busyAction === 'accept'}
                onClick={() => onAccept?.(swap)}
                data-testid={`swap-accept-${swap.id}`}
              >
                <Check size={15} className="me-1.5" />
                {t('Accept')}
              </Button>
            </>
          )}
          {canCancel && (
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              loading={busyAction === 'cancel'}
              onClick={() => onCancel?.(swap)}
              data-testid={`swap-cancel-${swap.id}`}
            >
              {t('Cancel request')}
            </Button>
          )}
        </div>
      )}

      {mode === 'approval' && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            loading={busyAction === 'reject'}
            onClick={() => onReject?.(swap)}
            data-testid={`swap-reject-${swap.id}`}
          >
            <X size={15} className="me-1.5" />
            {t('Reject')}
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={busy}
            loading={busyAction === 'approve'}
            onClick={() => onApprove?.(swap)}
            data-testid={`swap-approve-${swap.id}`}
          >
            <Check size={15} className="me-1.5" />
            {t('Approve')}
          </Button>
        </div>
      )}
    </article>
  );
};

export default SwapCard;
